import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export type AppCrashFallbackProps = {
  eventId?: string | null;
  onRetry?: () => void;
  variant?: "crash" | "update";
};

function reloadPage(): void {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

export function AppCrashFallback({ eventId, onRetry, variant = "crash" }: AppCrashFallbackProps) {
  const supportId = eventId?.trim() ? eventId : "unavailable";
  const retry = onRetry ?? reloadPage;
  const isUpdate = variant === "update";
  // Route to a human: the support ID travels in the mailto so a user never has
  // to copy it by hand — the email arrives pre-filled and traceable.
  const supportMailto =
    "mailto:support@deploycompare.com" +
    `?subject=${encodeURIComponent(`DeployCompare crash report (support ID ${supportId})`)}` +
    `&body=${encodeURIComponent(
      `Support ID: ${supportId}\n\nWhat I was doing when the app crashed:\n`
    )}`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12 text-foreground">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {isUpdate ? "Update in progress" : "Recovery mode"}
          </p>
          <CardTitle className="text-2xl">
            {isUpdate
              ? "DeployCompare is still updating."
              : "DeployCompare hit an unexpected problem."}
          </CardTitle>
          <CardDescription className="text-sm leading-6">
            {isUpdate ? (
              "A new version is still reaching your browser. Wait a minute, then reload the page to pick up the fresh app bundle."
            ) : (
              <>
                Try restoring the app. If this keeps happening, email the support ID to{" "}
                <a className="text-primary hover:underline" href={supportMailto}>
                  support@deploycompare.com
                </a>{" "}
                so we can trace the failure without asking for screenshots or console logs.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-xl border border-border bg-surface-2 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Support ID
            </p>
            <p className="mt-2 font-mono text-sm text-foreground">{supportId}</p>
          </div>
          <p className="text-sm leading-6 text-muted-foreground">
            {isUpdate
              ? "Your data is safe; this screen usually means the app deployed while this tab was open."
              : "The app state may be incomplete right now. Retrying usually restores the last screen. Reload the page if the retry path does not recover cleanly."}
          </p>
        </CardContent>
        <CardFooter className="flex flex-col items-stretch gap-3 sm:flex-row sm:justify-end">
          <Button onClick={reloadPage} type="button" variant="ghost">
            Reload page
          </Button>
          {!isUpdate ? (
            <Button onClick={retry} type="button">
              Try again
            </Button>
          ) : null}
        </CardFooter>
      </Card>
    </div>
  );
}
